import { useEffect, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useNetworkStatus } from 'technical/network/use-network-status';
import logger from 'technical/logger';

type OfflineQueryService<TData> = {
  set: (data: TData) => Promise<void>;
  get: () => Promise<TData | undefined>;
};

export type UseOnlineOfflineQueryOptions<TData, TError> =
  | {
      useQueryResult: UseQueryResult<TData, TError>;
      offlineService: OfflineQueryService<TData>;
      forceNetwork?: false;
    }
  | {
      useQueryResult: UseQueryResult<TData, TError>;
      offlineService?: OfflineQueryService<TData>;
      forceNetwork: true;
    };

export type UseOnlineOfflineQueryResult<TData, TError> = {
  data: TData | undefined;
  isLoading: boolean;
  error: TError | Error | null;
  source: 'network' | 'cache';
};

export const useOnlineOfflineQuery = <TData, TError>({
  useQueryResult,
  offlineService,
  forceNetwork = false,
}: UseOnlineOfflineQueryOptions<TData, TError>): UseOnlineOfflineQueryResult<
  TData,
  TError
> => {
  const { isOnline } = useNetworkStatus();
  const shouldUseNetwork = isOnline || forceNetwork;

  const [cachedData, setCachedData] = useState<TData | undefined>(undefined);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheError, setCacheError] = useState<Error | null>(null);

  useEffect(() => {
    if (shouldUseNetwork && useQueryResult.data && offlineService) {
      offlineService
        .set(useQueryResult.data)
        .catch(() => logger.warn('Failed to save data to cache'));
    }
  }, [shouldUseNetwork, useQueryResult.data, offlineService]);

  useEffect(() => {
    if (shouldUseNetwork || !offlineService) {
      return;
    }

    setCacheLoading(true);
    offlineService
      .get()
      .then((data) => {
        setCachedData(data);
        setCacheError(null);
      })
      .catch((err) => {
        setCacheError(
          err instanceof Error ? err : new Error('Failed to read from cache'),
        );
      })
      .finally(() => {
        setCacheLoading(false);
      });
  }, [shouldUseNetwork, offlineService]);

  if (shouldUseNetwork) {
    return {
      data: useQueryResult.data,
      isLoading: useQueryResult.isLoading,
      error: useQueryResult.error as Error | null,
      source: 'network',
    };
  }

  return {
    data: cachedData,
    isLoading: cacheLoading,
    error: cacheError,
    source: 'cache',
  };
};
